/** InsCoder WANG.J 2026-10-06 B + Static review navigation and Shopify editor lifecycle **/
(() => {
  if (customElements.get('cust-review-banner')) return;

  // InsCoder WANG.J 2026-10-07: Read the same public feeds used by the reviews page.
  // Share one request per page; failed loads can be retried after an editor reload.
  const reviewRequests = new Map();
  async function readResponse(url, json = true) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
      if (!response.ok) throw new Error('Reviews request failed');
      return await (json ? response.json() : response.text());
    } finally { clearTimeout(timeout); }
  }

  async function loadReviews(pageUrl) {
    const url = new URL(pageUrl, location.origin);
    if (url.origin !== location.origin) throw new Error('Reviews page must be on this storefront');
    if (reviewRequests.has(url.href)) return reviewRequests.get(url.href);
    const request = (async () => {
      const html = await readResponse(url.href, false);
      const page = new DOMParser().parseFromString(html, 'text/html');
      const panels = [...page.querySelectorAll('.cust-reviews-section__panel')];
      const scripts = [...page.querySelectorAll('script')].map((script) => script.textContent).join('\n');
      const init = scripts.match(/StampedFn\.init\(\s*\{([^}]+)\}/)?.[1] || '';
      const key = init.match(/apiKey\s*:\s*['"]([^'"]+)['"]/)?.[1];
      const store = init.match(/storeUrl\s*:\s*['"](\d+)['"]/)?.[1];
      const providerScript = [...page.querySelectorAll('script[src]')].find((script) => {
        const source = new URL(script.getAttribute('src'), url);
        return source.hostname === 'cdn.grw.reputon.com' && source.pathname === '/assets/widget.js';
      });
      const shop = providerScript ? new URL(providerScript.getAttribute('src'), url).searchParams.get('shop') : null;
      const stampedUrl = new URL('https://stamped.io/api/widget');
      stampedUrl.search = new URLSearchParams({ page: '1', apiKey: key || '', sId: store || '', take: '5' });
      const googleUrl = new URL('https://grw.reputon.com/app/storefront/widget');
      googleUrl.search = new URLSearchParams({ shop: shop || '', lang: page.documentElement.lang || 'en' });
      const [stamped, google] = await Promise.allSettled([
        key && store ? readResponse(stampedUrl.href) : Promise.resolve(null),
        shop ? readResponse(googleUrl.href) : Promise.resolve(null)
      ]);
      const website = stamped.status === 'fulfilled' && typeof stamped.value?.widget === 'string'
        ? [...new DOMParser().parseFromString(stamped.value.widget, 'text/html').querySelectorAll('.stamped-review')].map((review) => ({
          title: review.querySelector('.stamped-review-header-title')?.textContent.trim() || '',
          body: review.querySelector('.stamped-review-content-body')?.textContent.trim() || '',
          author: review.querySelector('.author')?.textContent.trim() || '',
          rating: Number(review.dataset.rating), verified: review.dataset.verified === 'buyer'
        })) : [];
      const businesses = google.status === 'fulfilled' && Array.isArray(google.value?.business) ? google.value.business : [];
      const groups = panels.map((panel) => {
        if (panel.querySelector('#stamped-main-widget')) return website;
        const widget = panel.querySelector('.reputon-google-reviews-widget');
        const index = Number(widget?.dataset.contentIndex) - 1;
        const reviews = businesses[index]?.reviews;
        return (Array.isArray(reviews) ? reviews : []).filter((review) => review && typeof review === 'object').map((review) => ({
          title: '', body: typeof review.text === 'string' ? review.text.trim() : '',
          author: typeof review.authorName === 'string' ? review.authorName.trim() : '',
          rating: Number(review.rating), verified: false
        }));
      }).map((group) => group.filter((review) => (review.title || review.body) && review.rating >= 1 && review.rating <= 5));
      const mixed = [];
      for (let offset = 0; mixed.length < 5 && groups.some((group) => group[offset]); offset += 1) {
        for (const group of groups) {
          if (group[offset]) mixed.push(group[offset]);
          if (mixed.length === 5) break;
        }
      }
      if (!mixed.length) throw new Error('No reviews available');
      return mixed;
    })();
    reviewRequests.set(url.href, request);
    request.catch(() => reviewRequests.delete(url.href));
    return request;
  }

  class ReviewBanner extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-review-track]');
      if (!this.track) return;
      this.cards = [...this.track.querySelectorAll('[data-review-card]')];
      this.dots = [...this.querySelectorAll('[data-review-index]')];
      this.currentIndex = 0;
      this.mobile = window.matchMedia('(max-width: 768px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.events = new AbortController();
      const options = { signal: this.events.signal };

      this.querySelector('[data-review-previous]').addEventListener('click', () => this.show(this.currentIndex - 1), options);
      this.querySelectorAll('[data-review-next]').forEach((control) => control.addEventListener('click', () => this.show(this.currentIndex + 1), options));
      this.track.addEventListener('scroll', () => {
        if (!this.mobile.matches) return;
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.updateFromScroll());
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.cards.length - 1 : this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1);
        this.show(next);
      }, options);
      this.mobile.addEventListener('change', () => this.resetLayout(), options);
      this.reducedMotion.addEventListener('change', () => this.finishTransition?.(), options);
      this.resizeObserver = new ResizeObserver(() => {
        if (this.layoutWidth !== this.track.clientWidth) this.resetLayout();
      });
      this.resizeObserver.observe(this.track);
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.cards.findIndex((card) => card.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.show(index, false);
      }, options);
      this.querySelectorAll('[data-review-previous], [data-review-next], [data-review-pagination]').forEach((control) => { control.hidden = this.cards.length < 2; });
      this.classList.add('is-ready');
      this.resetLayout();
      if (this.hasAttribute('data-auto-reviews')) this.loadAutomaticReviews();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.finishTransition?.();
      cancelAnimationFrame(this.scrollFrame);
      if (this.cards && this.track) this.cards.forEach((card) => this.track.append(card));
    }

    async loadAutomaticReviews() {
      const signal = this.events.signal;
      const status = this.querySelector('[data-review-load-status]');
      status.hidden = false;
      status.textContent = status.dataset.loadingMessage;
      status.classList.add('is-loading');
      this.track.setAttribute('aria-busy', 'true');
      try {
        const reviews = await loadReviews(this.dataset.reviewsPageUrl);
        if (signal.aborted) return;
        const template = this.querySelector('[data-review-template]');
        this.finishTransition?.();
        this.track.replaceChildren();
        reviews.forEach((review) => {
          const card = template.content.firstElementChild.cloneNode(true);
          const stars = card.querySelector('.cust-review-banner__stars');
          const star = stars.firstElementChild.cloneNode(true);
          stars.replaceChildren(...Array.from({ length: Math.round(review.rating) }, () => star.cloneNode(true)));
          stars.setAttribute('aria-label', stars.dataset.ratingTemplate.replace('[rating]', review.rating));
          const title = card.querySelector('.cust-review-banner__title');
          title.textContent = review.title;
          title.hidden = !review.title;
          card.querySelector('.cust-review-banner__body').textContent = review.body;
          card.querySelector('[data-review-author]').textContent = review.author;
          card.querySelector('[data-review-verified]').hidden = !review.verified;
          this.track.append(card);
        });
        this.cards = [...this.track.querySelectorAll('[data-review-card]')];
        this.dataset.reviewCount = this.cards.length;
        const pagination = this.querySelector('.cust-review-banner__pagination-button');
        pagination.replaceChildren(...this.cards.map((card, index) => {
          const dot = document.createElement('span');
          dot.className = 'cust-review-banner__dot';
          dot.dataset.reviewIndex = index;
          dot.setAttribute('aria-hidden', 'true');
          return dot;
        }));
        this.dots = [...pagination.children];
        this.querySelectorAll('[data-review-previous], [data-review-next], [data-review-pagination]').forEach((control) => { control.hidden = this.cards.length < 2; });
        status.hidden = true;
        this.resetLayout();
      } catch (error) {
        if (!signal.aborted) status.textContent = status.dataset.errorMessage;
      } finally {
        if (!signal.aborted) {
          status.classList.remove('is-loading');
          this.track.removeAttribute('aria-busy');
        }
      }
    }

    resetLayout() {
      this.finishTransition?.();
      this.layoutWidth = this.track.clientWidth;
      this.cards.forEach((card) => this.track.append(card));
      this.track.scrollLeft = 0;
      this.show(!this.mobile.matches && this.cards.length >= 3 ? 1 : 0, false);
    }

    show(index, announce = true) {
      if (!this.cards.length) return;
      this.finishTransition?.();
      const previousIndex = this.currentIndex;
      this.currentIndex = (index + this.cards.length) % this.cards.length;
      if (this.mobile.matches) {
        const card = this.cards[this.currentIndex];
        const first = this.cards[0];
        this.track.scrollTo({ left: card.offsetLeft - first.offsetLeft, behavior: announce && !this.reducedMotion.matches ? 'smooth' : 'instant' });
      } else {
        const direction = index - previousIndex;
        if (announce && !this.reducedMotion.matches && Math.abs(direction) === 1 && this.cards.length > 1) {
          this.slideDesktop(direction);
        } else this.orderDesktopCards();
      }
      this.updatePagination(announce);
    }

    orderDesktopCards() {
      // InsCoder WANG.J 2026-10-07: The selected desktop review occupies the middle slot.
      const start = this.currentIndex - (this.cards.length >= 3 ? 1 : 0);
      for (let offset = 0; offset < this.cards.length; offset += 1) {
        this.track.append(this.cards[(start + offset + this.cards.length) % this.cards.length]);
      }
      this.track.scrollLeft = 0;
    }

    // InsCoder WANG.J 2026-10-07: Slide through wraparound before updating the DOM order.
    slideDesktop(direction) {
      const first = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = parseFloat(getComputedStyle(first).width) + gap;
      const clone = (direction > 0 ? first : this.track.lastElementChild).cloneNode(true);
      clone.removeAttribute('data-review-card');
      clone.removeAttribute('data-review-active');
      clone.removeAttribute('data-block-id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      if (direction > 0) this.track.append(clone);
      else this.track.prepend(clone);
      this.track.scrollLeft = 0;

      const frames = direction > 0
        ? [{ translate: '0px' }, { translate: `-${distance}px` }]
        : [{ translate: `-${distance}px` }, { translate: '0px' }];
      const animations = [...this.track.children].map((card) => card.animate(frames, {
        duration: 300, easing: 'ease', fill: 'forwards'
      }));
      this.finishTransition = () => {
        this.finishTransition = null;
        animations[0].onfinish = null;
        animations.forEach((animation) => animation.cancel());
        clone.remove();
        this.orderDesktopCards();
      };
      animations[0].onfinish = () => this.finishTransition?.();
    }

    updateFromScroll() {
      if (!this.cards.length) return;
      const first = this.cards[0];
      this.currentIndex = this.cards.reduce((closest, card, index) => {
        const distance = Math.abs(card.offsetLeft - first.offsetLeft - this.track.scrollLeft);
        const previousDistance = Math.abs(this.cards[closest].offsetLeft - first.offsetLeft - this.track.scrollLeft);
        return distance < previousDistance ? index : closest;
      }, 0);
      this.updatePagination(false);
    }

    updatePagination(announce) {
      this.cards.forEach((card, index) => card.toggleAttribute('data-review-active', index === this.currentIndex));
      this.dots.forEach((dot, index) => {
        if (index === this.currentIndex) dot.setAttribute('data-current', 'true');
        else dot.removeAttribute('data-current');
      });
      if (announce) {
        const status = this.querySelector('[data-review-status]');
        status.textContent = status.dataset.statusTemplate.replace('[index]', this.currentIndex + 1).replace('[count]', this.cards.length);
      }
    }
  }

  customElements.define('cust-review-banner', ReviewBanner);
})();
/** InsCoder WANG.J 2026-10-06 E + Static review navigation and Shopify editor lifecycle **/
